if(!customElements.get('product-card')){
    class ProductCard extends HTMLElement {
        constructor(){
            super();

            this.imageWrapper = this.querySelector('.product-card__image');
            this.contentWrapper = this.querySelector(".product-card__content");

            const mediaJson = this.querySelector('.product-card-media-json');
            this.productImages = mediaJson ? JSON.parse(mediaJson.textContent) : [];

            const variantsJson = this.querySelector('.product-card-variants-json');
            this.productVariants = variantsJson ? JSON.parse(variantsJson.textContent) : null;
            this.mediaUpdateToken = 0;
            this.requestToken = 0;
            
            this.productForm = this.querySelector('product-card-form');
            if(this.productForm){
                this.productFormId = this.productForm.dataset.id;
            }
        }

        connectedCallback(){
            this.onSwatchValueChangeUnsubscriber = PubSub.subscribe(
                PubSub.events.productCardSwatchValueChange, this.handleSwatchValueChange.bind(this)
            );
            this.onSizeValueChangeUnsubscriber = PubSub.subscribe(
                PubSub.events.productCardSizeValueChange, this.handleSizeValueChange.bind(this)
            );

            // product card image slider
            this.initializeSwiper();
        }

        disconnectedCallback() {
            if(this.onSwatchValueChangeUnsubscriber && typeof this.onSwatchValueChangeUnsubscriber === 'function'){
                this.onSwatchValueChangeUnsubscriber();
            }
            if(this.onSizeValueChangeUnsubscriber && typeof this.onSizeValueChangeUnsubscriber === 'function'){
                this.onSizeValueChangeUnsubscriber();
            }
        }

        handleSizeValueChange({ data: { event, target, selectedOptionValues, source }}){
            if (!this.contains(event.target)) return;

            const productUrl = target.dataset.productUrl || this.pendingRequestUrl;
            const productId = target.dataset.optionValueId;
            this.pendingRequestUrl = productUrl;

            this.renderProductInfo({
                requestUrl: this.buildRequestUrl(productUrl, selectedOptionValues, target),
                targetId: productId,
                callback: this.updateProductInfo(true)
            });
        }

        handleSwatchValueChange({ data: { event, target, selectedOptionValues, source }}){
            if (!this.contains(event.target)) return;

            const productUrl = target.dataset.productUrl || this.pendingRequestUrl || url;
            const productId = target.dataset.optionValueId || id;
            this.pendingRequestUrl = productUrl;

            this.renderProductInfo({
                requestUrl: this.buildRequestUrl(productUrl, selectedOptionValues, target),
                targetId: productId,
                callback: this.updateProductInfo()
            });
        }

        buildRequestUrl(productUrl, selectedOptionValues, target){
            const variant = this.findSelectedVariant(productUrl, target);
            if(!variant){
                return this.buildRequestUrlWithParams(productUrl, selectedOptionValues);
            }

            const requestUrl = new URL(productUrl, window.location.origin);
            requestUrl.searchParams.delete('option_values');
            requestUrl.searchParams.set('variant', variant.id);
            requestUrl.searchParams.set('section_id', 'product-card');

            return `${requestUrl.pathname}${requestUrl.search}${requestUrl.hash}`;
        }

        // Shopify can resolve option_values to the wrong variant after a
        // product's option values are reordered, so request the variant by id.
        findSelectedVariant(productUrl, target){
            if(!this.productVariants || !target) return null;

            // A swatch in a combined listing can link to another product.
            const targetPath = new URL(productUrl, window.location.origin).pathname;
            if(targetPath !== this.productVariants.url) return null;

            const selectedOptions = Array.from(
                this.querySelectorAll('fieldset input:checked[data-option-position]')
            ).map(input => ({
                index: Number(input.dataset.optionPosition) - 1,
                value: input.value
            }));
            const targetIndex = Number(target.dataset.optionPosition) - 1;
            const variants = this.productVariants.variants;
            const matches = options => variant => options.every(option => {
                return variant.options[option.index] === option.value;
            });

            // Keep the other selected options when that combination exists.
            // Otherwise fall back to any variant with the clicked value.
            const exactMatches = variants.filter(matches(selectedOptions));
            const targetMatches = variants.filter(matches([{ index: targetIndex, value: target.value }]));
            const candidates = exactMatches.length ? exactMatches : targetMatches;

            return candidates.find(variant => variant.available) || candidates[0] || null;
        }

        updateProductInfo(sizeUpdate = false){
            return(html, requestToken) => {
                const selectedVariantJsonData = JSON.parse(html.querySelector('#json-selected-variant').textContent);
                const cardMediaJson = html.querySelector('#json-product-card-media');
                const cardMedia = cardMediaJson ? JSON.parse(cardMediaJson.textContent) : null;
                const media = cardMedia?.selected
                    || selectedVariantJsonData?.featured_media
                    || selectedVariantJsonData?.featured_image;

                this.updateMedia(media, selectedVariantJsonData?.name || '', cardMedia, requestToken);
                this.updatePrice(html);
                this.updateComparePriceCheckbox(html);

                if(sizeUpdate){
                    const data = {
                        productFormId: this.productFormId
                    }

                    PubSub.publish(PubSub.events.sizeAddToCart, {
                        data: data
                    });
                }

                this.updateProductForm(html);
            }
        }

        buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false){
            const requestUrl = new URL(url, window.location.origin);

            if(!shouldFetchFullPage){
                requestUrl.searchParams.set('section_id', 'product-card');
            }

            if(optionValues.length){
                // product_option_value.product_url can already contain this
                // parameter. Replace it so the request has one unambiguous state.
                requestUrl.searchParams.set('option_values', optionValues.join(','));
            }else{
                requestUrl.searchParams.delete('option_values');
            }

            return `${requestUrl.pathname}${requestUrl.search}${requestUrl.hash}`;
        }

        renderProductInfo({ requestUrl, targetId, callback }){
            this.abortController?.abort();
            this.abortController = new AbortController();
            const requestToken = ++this.requestToken;

            this.setMediaLoading(true);

            fetch(requestUrl, { signal: this.abortController.signal })
                .then(res => res.text())
                .then(resText => {
                    this.pendingRequestUrl = null;
                    const html = new DOMParser().parseFromString(resText, 'text/html');
                    callback(html, requestToken);
                })
                .catch(error => {
                    if(error.name === 'AbortError') {
                        console.log('Fetch aborted by user');
                    }else{
                        console.error(error);
                    }

                    if(requestToken === this.requestToken){
                        this.setMediaLoading(false);
                    }
                });
        }

        updateMedia(media = null, variantName = '', cardMedia = null, requestToken = this.requestToken){
            if(!media){
                this.finishMediaLoading(requestToken);
                return;
            }

            // update swiper index if swiper exists
            if(this.swiper && media.id){
                const targetIndex = Array.from(this.slides).findIndex(slide => {
                    return parseInt(slide.dataset.mediaId) === media.id;
                });

                if(targetIndex >= 0 && this.swiper.activeIndex !== targetIndex){
                    const targetImage = this.slides[targetIndex]?.querySelector('img');

                    if(targetImage && !targetImage.complete){
                        const finishLoading = () => {
                            if(requestToken !== this.requestToken) return;
                            this.finishMediaLoading(requestToken);
                        };

                        targetImage.addEventListener('load', finishLoading, { once: true });
                        targetImage.addEventListener('error', finishLoading, { once: true });
                        this.swiper.slideTo(targetIndex);
                        return;
                    }

                    this.swiper.slideTo(targetIndex);
                }

                this.finishMediaLoading(requestToken);
                return;
            }

            const baseImage = this.querySelector('.product-card__base-img');
            const hoverImage = this.querySelector('.product-card__hover-img');
            const hoverImageWrapper = this.querySelector('.product-card__hover-image-wrapper');
            const selectedImageIndex = this.productImages.findIndex(image => {
                return String(image.id) === String(media.id);
            });

            if(!baseImage){
                this.finishMediaLoading(requestToken);
                return;
            }

            // Section-rendered media also works when a swatch links to another
            // product in a combined listing.
            const selectedImage = cardMedia?.selected || this.productImages[selectedImageIndex];
            if(!selectedImage){
                this.finishMediaLoading(requestToken);
                return;
            }

            // Product images are ordered in Shopify. The hover image must follow
            // the selected variant image, not the product's original main image.
            const nextImage = cardMedia
                ? cardMedia.hover
                : this.productImages[selectedImageIndex + 1];
            const updateToken = ++this.mediaUpdateToken;
            const preloadedImage = new Image();

            preloadedImage.addEventListener('load', () => {
                // Ignore a stale image load after the customer selects another swatch.
                if(updateToken !== this.mediaUpdateToken || requestToken !== this.requestToken) return;

                // Shopify's generated srcset must be removed or the browser may keep the old image.
                baseImage.removeAttribute('srcset');
                baseImage.removeAttribute('sizes');
                baseImage.src = selectedImage.src;
                baseImage.alt = variantName || selectedImage.alt || '';

                if(hoverImage && hoverImageWrapper && nextImage){
                    hoverImage.removeAttribute('srcset');
                    hoverImage.removeAttribute('sizes');
                    hoverImage.src = nextImage.src;
                    hoverImage.alt = nextImage.alt || '';
                    hoverImageWrapper.classList.remove('hidden');
                }else if(hoverImageWrapper){
                    hoverImageWrapper.classList.add('hidden');
                }

                this.finishMediaLoading(requestToken);
            }, { once: true });

            preloadedImage.addEventListener('error', () => {
                this.finishMediaLoading(requestToken);
            }, { once: true });

            preloadedImage.src = selectedImage.src;
        }

        setMediaLoading(isLoading){
            if(!this.imageWrapper) return;

            this.imageWrapper.classList.toggle('is-loading', isLoading);
            this.imageWrapper.setAttribute('aria-busy', isLoading ? 'true' : 'false');
        }

        finishMediaLoading(requestToken){
            if(requestToken === this.requestToken){
                this.setMediaLoading(false);
            }
        }

        updatePrice(html){
            const newPriceHTML = html.querySelector('.price');
            const oldPrice = this.querySelector(".price");

            if(newPriceHTML && oldPrice && oldPrice.parentNode){
                oldPrice.parentNode.replaceChild(newPriceHTML.cloneNode(true), oldPrice);
            }
        }

        updateComparePriceCheckbox(html){
            const newComparePriceCheckboxHTML = html.querySelector('.compare-product-checkbox');
            const oldComparePriceCheckbox = this.querySelector('.compare-product-checkbox');
            
            if(newComparePriceCheckboxHTML && oldComparePriceCheckbox && oldComparePriceCheckbox.parentNode){
                oldComparePriceCheckbox.parentNode.replaceChild(newComparePriceCheckboxHTML.cloneNode(true), oldComparePriceCheckbox);
                return;
            }
        }

        updateProductForm(html){
            const newProductFormHTML = html.querySelector(".product-form");
            const oldProductForm = this.querySelector(".product-form");

            if(newProductFormHTML && oldProductForm && oldProductForm.parentNode){
                oldProductForm.parentNode.replaceChild(newProductFormHTML.cloneNode(true), oldProductForm);

                if(!this.productFormId) return;
                
                this.querySelectorAll('.product-form input[type="radio"]').forEach(radio => {
                    radio.setAttribute('form', this.productFormId);
                });
                this.querySelector('.product-form')?.setAttribute('data-id', this.productFormId);
                this.querySelectorAll(".product-form form").forEach(item => {
                    item.id = this.productFormId;
                });
            }
        }

        async initializeSwiper(){
            this.swiperContainer = this.querySelector('.product-card__image-wrapper .swiper-container');
            if(!this.swiperContainer) return;

            this.slides = this.swiperContainer.querySelectorAll('.swiper-slide');
            if(this.slides.length <= 1) return;

            if(typeof Swiper === 'undefined'){
                await new Promise(resolve => {
                    const check = () => {
                        if(typeof Swiper !== 'undefined') resolve();
                        else requestAnimationFrame(check);
                    };
                    check();
                });
            }

            const loop = false;

            const swiperSettings = {
                init: false,
                initialSlide: 0,
                loop: loop,
                centeredSlides: false,
                speed: 500,
                watchOverflow: true,
                observer: true,
                observeSlideChildren: true,
                autoHeight: false,
                on: {
                    init: () => {
                        const paginationBullets = this.querySelectorAll(".swiper-pagination-bullet");
                        if(paginationBullets.length > 0){
                            this.style.setProperty('--pagination-width', `calc(100% / ${paginationBullets.length})`);
                            paginationBullets.forEach((item, index) => {
                                item.setAttribute('data-index', `${index}`);
                            });

                            // set pagination color
                            const activeSlide = this.querySelector('.swiper-slide-active');
                            if(activeSlide){
                                const colorText = getComputedStyle(activeSlide).getPropertyValue('--color-text').trim();
                                this.style.setProperty('--color-pagination', `${colorText}`);
                            }
                            setTimeout(() => {
                                this.querySelector('.swiper-pagination')?.classList.add('loaded');
                                this.checkSwiperLock();
                            }, 500);
                        }
                    },
                    slideChange: () => {
                        const activePagination = this.querySelector(".swiper-pagination-bullet-active");
                        if(activePagination){
                            const activePaginationIndex = parseInt(activePagination.dataset.index);
                            this.style.setProperty('--pagination-translate-x', `calc(100% * ${activePaginationIndex})`);
                        }
                    },
                    transitionEnd: () => {
                        // set pagination color
                        const activeSlide = this.querySelector('.swiper-slide-active');
                        if(activeSlide){
                            const colorText = getComputedStyle(activeSlide).getPropertyValue('--color-text').trim();
                            this.style.setProperty('--color-pagination', `${colorText}`);
                        }

                        // check if active slide contains video and pause other videos
                        this.slides.forEach(slide => {
                            const video = slide.querySelector('video');
                            if(video){
                                if(slide.classList.contains('swiper-slide-active')){
                                    video.play();
                                }else{
                                    video.pause();
                                    video.currentTime = 0;
                                }
                            }
                        });
                    }
                }
            }

            if(this.querySelector(".swiper-navigation-wrapper")){
                swiperSettings.navigation = {
                    nextEl: this.querySelector('.button-next'),
                    prevEl: this.querySelector('.button-previous')
                }
            }

            if(this.querySelector('.swiper-pagination')){
                swiperSettings.pagination = {
                    el: this.querySelector('.swiper-pagination')
                }
            }

            this.swiper = new Swiper(this.swiperContainer, swiperSettings);
            this.swiper.init();

            // Mark clone slides as inert so keyboard focus skips duplicates
            this.swiper.slides.forEach(slide => {
                if(slide.classList.contains('swiper-slide-duplicate')){
                    slide.setAttribute('inert', '');
                    slide.setAttribute('aria-hidden', 'true');
                }
            });

            // Re-apply inert to clones after Swiper updates (loop mode re-creates clones)
            this.swiper.on("slideChange", () => {
                this.swiper.slides.forEach(slide => {
                    if(slide.classList.contains('swiper-slide-duplicate')){
                        slide.setAttribute('inert', '');
                        slide.setAttribute('aria-hidden', 'true');
                    }
                });
            });

            this.swiper.on("resize", this.checkSwiperLock.bind(this));

            this.setupFocusListeners();
        }


        checkSwiperLock(){
            if(!this.querySelector(".swiper-pagination")) return;
            
            if(this.swiper.isLocked){
                this.querySelector(".swiper-pagination").classList.add('hidden');
            }else{
                this.querySelector(".swiper-pagination").classList.remove('hidden');
            }
        }

        setupFocusListeners(){
            this.querySelectorAll(".button-next, .button-previous").forEach(button => {
                button.addEventListener('focus', () => {
                    button.classList.add('focused');
                    button.addEventListener('blur', () => {
                        button.classList.remove('focused');
                    }, { once: true });
                });
            });
        }
    }

    customElements.define('product-card', ProductCard);
}
